
import {
  Box,
  Stack,
  Button,
  Typography,
  Avatar,
  Paper,
  Divider,
} from '@mui/material';
import {
  Logout as LogoutIcon,
  DescriptionOutlined as DescriptionOutlinedIcon,
  ArrowBack as ArrowBackIcon,
  DeleteOutline as DeleteOutlineIcon,
  ChevronRight as ChevronRightIcon,
  Bolt as BoltIcon,
} from '@mui/icons-material';
import { ROUTES } from '../../routes';
import { useNavigate } from 'react-router-dom';

const getInitials = (name = '') => {
  const parts = name.trim().split(' ').filter(Boolean);
  if (!parts.length) return 'U';
  const first = parts[0][0] || '';
  const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
  return `${first}${last}`.toUpperCase() || 'U';
};

function MobileUserProfile({
  userInformation,
  credits,
  onTerms,
  onLogout,
  onBack,
  onDeleteClick,
  setShowUserProfile
}) {
  const navigate = useNavigate();
  return (
    <Box
      sx={{
        position: 'fixed',
        top: 40,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1000,
        bgcolor: 'var(--grey-50)',
        overflowY: 'auto',
        pb: 10,
        minHeight: 'calc(100vh - 56px)',
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          borderTop: '1px solid var(--bluegrey-400)',
          borderBottom: '1px solid var(--bluegrey-400)',
          position: 'sticky',
          top: 0,
          bgcolor: 'white',
          zIndex: 1,
        }}
      >
        <Box
          onClick={() => {
            setShowUserProfile(false);
            navigate(ROUTES.HISTORY);
          }}
          sx={{
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            gap: 1,
          }}
        >
          <ArrowBackIcon fontSize="small" sx={{ color: 'text.secondary' }} />
        </Box>
        <Typography variant="subtitle1" color="text.primary" sx={{ fontWeight: 600 }}>
          User Profile
        </Typography>
      </Box>
      <Box sx={{ maxWidth: 520, mx: 'auto', px: 2, pt: 2 }}>
        <Paper
          elevation={0}
          sx={{
            bgcolor: 'white',
            borderRadius: 2,
            boxShadow: '0px 8px 24px rgba(0,0,0,0.06)',
            overflow: 'hidden',
          }}
        >
          <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Avatar
                sx={{
                  width: 56,
                  height: 56,
                  bgcolor: 'var(--primary-500)',
                  color: 'white',
                  fontWeight: 700,
                }}
              >
                {getInitials(userInformation?.username)}
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="subtitle1" color="text.primary" fontWeight={700} noWrap>
                  {userInformation?.username || 'User Name'}
                </Typography>
                <Typography variant="body2" color="text.secondary" noWrap>
                  {userInformation?.company_name || 'Company Name'}
                </Typography>
              </Box>
            </Stack>

            <Divider />

            <Stack spacing={1.5}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="body2" color="text.secondary">
                  Company Profile
                </Typography>
                <Typography variant="body2" color="text.primary" fontWeight={600} noWrap>
                  {userInformation?.company_name || 'Company Name'}
                </Typography>
              </Stack>

              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="body2" color="text.secondary">
                  User Name
                </Typography>
                <Typography variant="body2" color="text.primary" fontWeight={600} noWrap>
                  {userInformation?.username || 'User Name'}
                </Typography>
              </Stack>

              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="body2" color="text.secondary">
                  Credits Used
                </Typography>
                <Typography variant="body2" color="text.primary" fontWeight={700}>
                  {credits === null ? 'Loading...' : `${credits} / 60`}
                </Typography>
              </Stack>
            </Stack>

            <Divider />
    
            {/* Account Setting Section */}
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  fontWeight: 600,
                  fontSize: '0.7rem',
                  px: 1,
                  mb: 1,
                  display: 'block',
                }}
              >
                Policy
              </Typography>
              <Box
                onClick={onTerms}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  py: 1.5,
                  px: 1.5,
                  cursor: 'pointer',
                  borderRadius: 2,
                  bgcolor: 'white',
                  border: '1px solid rgba(0,0,0,0.08)',
                  '&:hover': {
                    backgroundColor: 'var(--grey-50)',
                    borderColor: 'rgba(0,0,0,0.12)',
                  },
                  transition: 'all 0.2s',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <DescriptionOutlinedIcon sx={{ fontSize: '22px', color: 'text.primary' }} />
                  <Typography variant="body2" color="text.primary" fontWeight={500}>
                    Terms and Conditions
                  </Typography>
                </Box>
                <ChevronRightIcon sx={{ fontSize: '20px', color: 'text.secondary' }} />
              </Box>
            </Box>

            {/* Trash Section */}
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  fontWeight: 600,
                  fontSize: '0.7rem',
                  px: 1,
                  mb: 1,
                  display: 'block',
                }}
              >
                Trash
              </Typography>
              <Box
                onClick={onDeleteClick}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  py: 1.5,
                  px: 1.5,
                  cursor: 'pointer',
                  borderRadius: 2,
                  bgcolor: 'white',
                  border: '1px solid rgba(0,0,0,0.08)',
                  '&:hover': {
                    backgroundColor: 'var(--grey-50)',
                    borderColor: 'rgba(0,0,0,0.12)',
                  },
                  transition: 'all 0.2s',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <DeleteOutlineIcon sx={{ fontSize: '22px', color: 'text.primary' }} />
                  <Typography variant="body2" color="text.primary" fontWeight={500}>
                    Recently Deleted Items
                  </Typography>
                </Box>
                <ChevronRightIcon sx={{ fontSize: '20px', color: 'text.secondary' }} />
              </Box>
            </Box>

            {/* Logout Button */}
            <Box sx={{ pt: 2 }}>
              <Button
                fullWidth
                variant="contained"
                startIcon={<LogoutIcon />}
                onClick={onLogout}
                sx={{
                  textTransform: 'none',
                  bgcolor: 'error.main',
                  '&:hover': {
                    bgcolor: 'error.dark',
                  },
                  py: 1.5,
                  fontWeight: 600,
                  borderRadius: 2,
                }}
              >
                Log Out
              </Button>
            </Box>
          </Box>
        </Paper>

      </Box>
    </Box>
  );
}

export default MobileUserProfile;

